import moment from "moment";
import { api } from "common";
import { Column } from "material-table";
import { Typography } from "@mui/material";
import { ICarType } from "common/src/types";
import { useTranslation } from "react-i18next";
import BlockIcon from "@mui/icons-material/Block";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useSelector, useDispatch } from "react-redux";
import ImageColumn from "components/CustomTable/Column/ImageColumn";
import { CustomMaterialTable } from "components/CustomMaterialTable";
import { RequestPushMsg } from "common/src/other/NotificationFunctions";


export default function CarsList() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { updateUserCar, carform } = api;
  const auth = useSelector((state) => state.auth);
  const userdata = useSelector((state) => state.usersdata);
  const carlistdata = useSelector((state) => state.carlistdata);
  const settings = useSelector((state) => state.settingsdata.settings);

  if (!settings || !auth || !auth?.profile) return;

  const columns: Column<ICarType>[] = [
    {
      field: "active",
      type: "boolean",
      title: t("active_status"),
    },
    {
      field: "carType",
      title: t("car_type"),
      render: (rowData: ICarType) =>
        Array.isArray(rowData.carType)
          ? rowData.carType.join(", ")
          : rowData.carType,
    },
    {
      field: "createdAt",
      title: t("createdAt"),
      defaultSort: "desc",
      render: (rowData: ICarType) =>
        rowData.createdAt ? moment(rowData.createdAt).format('DD/MM/YYYY HH:mm') : null,
    },
    {
      field: "driverFirstName",
      title: t("first_name"),
    },

    {
      field: "driverLastName",
      title: t("last_name"),
    },
    {
      field: "driverMobile",
      title: t("mobile"),
    },
    ...(settings.required_car_image_frente ? [{
      title: t('front_photo'),
      field: "car_image_frente",
      hidden: !settings.required_car_image_frente,
      render: (rowData: ICarType) =>
        <ImageColumn
          imageUrl={rowData.car_image_frente}
          altText="car_image_frente"
        />

    }] : []),

    ...(settings.required_car_image_lateraldireita ? [{
      title: t('right_side_photo'),
      field: "car_image_lateraldireita",
      hidden: !settings.required_car_image_lateraldireita,
      render: (rowData: ICarType) =>
        <ImageColumn
          imageUrl={rowData.car_image_lateraldireita}
          altText="car_image_lateraldireita"
        />
    }] : []),

    ...(settings.required_car_image_lateralesquerda ? [{
      title: t('left_side_photo'),
      field: "car_image_lateralesquerda",
      hidden: !settings.required_car_image_lateralesquerda,
      render: (rowData: ICarType) =>
        <ImageColumn
          imageUrl={rowData.car_image_lateralesquerda}
          altText="car_image_lateralesquerda"
        />
    }] : []),

    ...(settings.required_car_image_traseira ? [{
      title: t('rear_photo'),
      field: "car_image_traseira",
      hidden: !settings.required_car_image_traseira,
      render: (rowData: ICarType) =>
        <ImageColumn
          imageUrl={rowData.car_image_traseira}
          altText="car_image_traseira"
        />
    }] : []),

    ...(settings.required_car_image_documento ? [{
      title: t('document_photo'),
      field: "car_image_documento",
      hidden: !settings.required_car_image_documento,
      render: (rowData: ICarType) =>
        <ImageColumn
          imageUrl={rowData.car_image_documento}
          altText="car_image_documento"
        />
    }] : []),
    { title: t("vehicle_reg_no"), field: "vehicleNumber" },
    { title: t("vehicle_model_name"), field: "vehicleMake" },
    { title: t("vehicle_model_no"), field: "vehicleModel" },
    { title: t('vehicle_year'), field: "vehicleYear" },
    { title: t("other_info"), field: "other_info" },
  ];

  return (
    <CustomMaterialTable
      title={"cars"}
      columns={columns}
      data={carlistdata.cars?.filter(car => car.driverFirstName) ?? []}
      options={{
        rowStyle: (rowData) => {
          if (!rowData.approved && settings.carApproval) {
            return {
              filter: "drop-shadow(0 0 10px red)",
            };
          }
          return {};
        },
      }}
      {...(settings.carApproval ? {
        actions: [
          (rowData: ICarType) => ({
            icon: () => (
              <div
                style={{
                  display: "flex",
                  padding: "10px",
                  cursor: "pointer",
                  alignItems: "center",
                }}
              >
                {rowData.approved ? <BlockIcon /> : <DoneAllIcon />}
                <Typography variant="subtitle2" style={{ marginLeft: "8px" }}>
                  {t(rowData.approved ? "reject" : "accept")}
                </Typography>
              </div>
            ),
            onClick: (_, rowData) => {
              if (Array.isArray(rowData)) return;

              const driver = userdata.users?.find(
                user => user.id == rowData.driver
              );

              if (!driver) return;

              const newApprovedStatus = !rowData.approved;

              if (rowData.active) {
                dispatch(
                  updateUserCar(rowData.driver, {
                    carApproved: newApprovedStatus,
                  })
                );
              }

              carform(
                { ...rowData, approved: newApprovedStatus },
                "edit"
              );

              if (driver.pushToken) {
                RequestPushMsg(driver.pushToken, {
                  ios: driver.userPlatform === "IOS",
                  title: newApprovedStatus ? t("NEWS") : t("ATTENTION"),
                  msg: newApprovedStatus
                    ? t("YOUR_CAR_HAS_BEEN_APPROVED")
                    : t("YOUR_CAR_WAS_REJECTED"),
                });
              }
            },
          }),
        ],
      } : {})}
    />
  );
}
